import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Select, Spin, Typography } from "antd";
import { fetchAgentPublicLeads } from "../../../services/publicSiteLeadsApi";
import { isAdminUser } from "../../../services/authService";
import {
  formatPublicLeadCategory,
  isCrmDocketLead,
} from "../../Agent/Crm/crmLeadFormatters";
import "./DashboardMyDucatsCard.scss";

const { Text } = Typography;

const LIMIT_OPTIONS = [
  { value: 3, label: "3" },
  { value: 5, label: "5" },
  { value: 10, label: "10" },
];

const DOCKETS_HREF = "/p/dashboard/drafts?tab=dockets";

function formatFileRef(lead) {
  const id = String(lead?.id || "").replace(/-/g, "");
  if (id.length >= 6) return `HR-${id.slice(0, 6).toUpperCase()}`;
  return "HR-LEAD";
}

function formatShortDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("he-IL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
}

function docketTitle(row) {
  const name = String(row?.full_name || "").trim();
  if (name) return name;
  return formatFileRef(row);
}

function docketSnippet(row) {
  const dest = String(row?.interest_destination || "").trim();
  const category = formatPublicLeadCategory(row?.category);
  const parts = [dest || null, category !== "כללי" ? category : null].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  const note = String(row?.ai_sales_note || row?.message || "").trim();
  return note || "";
}

export default function DashboardMyDucatsCard() {
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(true);
  const [dockets, setDockets] = useState([]);
  const isAdmin = isAdminUser();

  const loadDockets = useCallback(async () => {
    setLoading(true);
    try {
      // Admin: all agents' dockets. kind=dockets filters server-side so recent
      // prospect leads do not crowd out completed-order files in the LIMIT window.
      const res = await fetchAgentPublicLeads({
        limit: 50,
        scope: isAdmin ? "all" : undefined,
        kind: "dockets",
      });
      const leads = Array.isArray(res?.leads) ? res.leads : [];
      setDockets(leads.filter(isCrmDocketLead));
    } catch {
      setDockets([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    void loadDockets();
  }, [loadDockets]);

  const items = useMemo(() => dockets.slice(0, limit), [dockets, limit]);
  const empty = !loading && items.length === 0;

  return (
    <section className="dashboard-my-ducats" dir="rtl" aria-label="הדוקטים שלי">
      <div className="dashboard-my-ducats__head">
        <h2 className="dashboard-my-ducats__title">הדוקטים שלי</h2>
        <div className="dashboard-my-ducats__tools">
          <Text type="secondary" className="dashboard-my-ducats__limit-label">
            הצג עד
          </Text>
          <Select
            size="small"
            className="dashboard-my-ducats__limit-select"
            value={limit}
            options={LIMIT_OPTIONS}
            onChange={(v) => setLimit(v)}
            popupMatchSelectWidth={false}
          />
          <Link to={DOCKETS_HREF} className="dashboard-my-ducats__link-all">
            לכל הדוקטים
          </Link>
        </div>
      </div>

      <div className="dashboard-my-ducats__body">
        {loading ? (
          <div className="dashboard-my-ducats__loading">
            <Spin size="small" />
          </div>
        ) : empty ? (
          <div className="dashboard-my-ducats__empty">אין דוקטים להצגה</div>
        ) : (
          <ul className="dashboard-my-ducats__list">
            {items.map((row) => (
              <li key={row.id} className="dashboard-my-ducats__item">
                <Link
                  to={`${DOCKETS_HREF}&leadId=${encodeURIComponent(String(row.id))}`}
                  className="dashboard-my-ducats__item-main"
                >
                  <div className="dashboard-my-ducats__item-title">{docketTitle(row)}</div>
                  <div className="dashboard-my-ducats__item-meta">
                    {[formatFileRef(row), formatShortDate(row.created_at)]
                      .filter(Boolean)
                      .join(" · ")}
                    {isAdmin && row.agent_name
                      ? ` · ${row.agent_name}`
                      : isAdmin && row.agent_id != null
                        ? ` · סוכן #${row.agent_id}`
                        : ""}
                  </div>
                  {docketSnippet(row) ? (
                    <div className="dashboard-my-ducats__item-snippet">{docketSnippet(row)}</div>
                  ) : null}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
